/**
 * BBQ Stove — Responsive Correction Layer
 * Version 1.0.1
 *
 * Adds the small behavioural pieces that CSS alone cannot safely provide:
 * - transparent hero header becomes solid after scrolling;
 * - mobile footer groups begin collapsed, desktop groups stay expanded;
 * - fallback class removes duplicate Custom Liquid wrapper padding;
 * - newsletter label is corrected for screen-reader users;
 * - Prep Stations portrait-tablet layout is forced directly on the three
 *   image-led sections when Samsung/Android desktop-mode viewport reporting
 *   prevents the CSS tablet breakpoints from activating.
 */

(() => {
  'use strict';

  if (document.documentElement.dataset.bbqResponsiveReady === 'true') return;
  document.documentElement.dataset.bbqResponsiveReady = 'true';

  const MOBILE_QUERY = '(max-width: 749px)';

  const OVERLAY_HERO_SELECTOR = [
    '.bbq-hero-video',
    '.bbq-range-hero',
    '.bbq-workshop-hero'
  ].join(', ');

  const setCustomLiquidWrappers = (root = document) => {
    const wrappers = root.querySelectorAll(
      '.section > .gradient > div[class^="section-"][class$="-padding"]'
    );

    wrappers.forEach((wrapper) => {
      const bespokeSection = Array.from(wrapper.children).some((child) => {
        return Array.from(child.classList || []).some((className) => {
          return className.startsWith('bbq-');
        });
      });

      wrapper.classList.toggle(
        'bbq-custom-liquid--full-bleed',
        bespokeSection
      );
    });
  };

  const initialiseOverlayHeader = () => {
    const header = document.getElementById('shopify-section-header');
    const overlayHero = document.querySelector(OVERLAY_HERO_SELECTOR);

    if (!header || !overlayHero) return;

    document.body.classList.add('bbq-has-overlay-header');

    let frameId = 0;

    const updateScrolledState = () => {
      frameId = 0;

      document.body.classList.toggle(
        'bbq-header-scrolled',
        window.scrollY > 24
      );
    };

    const requestUpdate = () => {
      if (frameId) return;

      frameId = window.requestAnimationFrame(updateScrolledState);
    };

    updateScrolledState();

    window.addEventListener('scroll', requestUpdate, {
      passive: true
    });

    const menu = document.getElementById(
      'Details-menu-drawer-container'
    );

    if (menu) {
      menu.addEventListener('toggle', () => {
        document.body.classList.toggle(
          'bbq-menu-open',
          menu.open
        );
      });
    }
  };

  let footerListenerAttached = false;

  const initialiseFooter = () => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);

    const syncFooterMode = () => {
      document
        .querySelectorAll('.bbq-footer__menu-column')
        .forEach((details) => {
          details.open = !mediaQuery.matches;
        });
    };

    const fixNewsletterLabel = () => {
      document
        .querySelectorAll('label[for^="BBQFooterEmail-"]')
        .forEach((label) => {
          label.textContent = 'Email address';
        });
    };

    syncFooterMode();
    fixNewsletterLabel();

    if (!footerListenerAttached) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener(
          'change',
          syncFooterMode
        );
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(syncFooterMode);
      }

      footerListenerAttached = true;
    }
  };

  /* ------------------------------------------------------------------------
     PREP STATIONS TABLET FORCE

     Samsung Chrome can use a desktop-style layout viewport on tablets.
     That means max-width media queries can miss the device completely.

     This runs only on the Prep Stations page and only when the device:
     - has touch input;
     - has a tablet-sized shortest screen edge;
     - is being held in a portrait-like viewport;
     - has a viewport wider than the mobile breakpoint.

     Styles are applied directly with !important, so the inline <style>
     blocks inside Shopify Custom Liquid sections cannot override them.
     ------------------------------------------------------------------------ */

  let prepResizeListenerAttached = false;
  let prepResizeFrame = 0;

  const setImportant = (element, property, value) => {
    if (!element) return;

    element.style.setProperty(property, value, 'important');
    element.dataset.bbqPrepTabletForced = 'true';
  };

  const setImportantAll = (selector, styles) => {
    document.querySelectorAll(selector).forEach((element) => {
      Object.entries(styles).forEach(([property, value]) => {
        setImportant(element, property, value);
      });
    });
  };

  const clearForcedPrepStyles = () => {
    document
      .querySelectorAll('[data-bbq-prep-tablet-forced="true"]')
      .forEach((element) => {
        [
          'display',
          'grid-template-columns',
          'align-items',
          'order',
          'position',
          'inset',
          'width',
          'max-width',
          'min-width',
          'height',
          'min-height',
          'max-height',
          'margin',
          'margin-left',
          'margin-right',
          'padding',
          'gap',
          'overflow',
          'object-fit',
          'object-position',
          'font-size',
          'line-height',
          'overflow-wrap',
          'word-break'
        ].forEach((property) => {
          element.style.removeProperty(property);
        });

        delete element.dataset.bbqPrepTabletForced;
      });

    document.documentElement.classList.remove(
      'bbq-prep-tablet-layout-forced'
    );
  };

  const shouldForcePrepTabletLayout = () => {
    const path = window.location.pathname.replace(/\/+$/, '');

    if (path !== '/pages/prep-stations') return false;

    const touchPoints = Number(navigator.maxTouchPoints || 0);
    const screenWidth = Number(window.screen?.width || window.innerWidth);
    const screenHeight = Number(window.screen?.height || window.innerHeight);
    const shortestScreenSide = Math.min(screenWidth, screenHeight);

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const portraitLikeViewport =
      viewportHeight >= viewportWidth * 0.78;

    return (
      touchPoints > 0 &&
      shortestScreenSide >= 600 &&
      viewportWidth >= 750 &&
      viewportWidth <= 2200 &&
      portraitLikeViewport
    );
  };

  const forcePrepTabletLayout = () => {
    if (!document.querySelector('.bbq-prep-hero')) return;

    if (!shouldForcePrepTabletLayout()) {
      clearForcedPrepStyles();
      return;
    }

    document.documentElement.classList.add(
      'bbq-prep-tablet-layout-forced'
    );

    const viewportWidth = window.innerWidth;

    const horizontalPadding =
      viewportWidth <= 1000 ? '32px' : '44px';

    const heroHeight = `${Math.min(
      Math.max(viewportWidth * 0.56, 430),
      680
    )}px`;

    const purposeHeight = `${Math.min(
      Math.max(viewportWidth * 0.56, 420),
      650
    )}px`;

    const workshopHeight = `${Math.min(
      Math.max(viewportWidth * 0.64, 500),
      760
    )}px`;

    /* ========================================================
       HERO
       ======================================================== */

    setImportantAll(
      [
        '.bbq-prep-hero',
        '.bbq-prep-hero__inner',
        '.bbq-prep-hero__content',
        '.bbq-prep-hero__content-inner',
        '.bbq-prep-hero__visual'
      ].join(', '),
      {
        width: '100%',
        'max-width': '100%',
        'min-width': '0'
      }
    );

    setImportantAll('.bbq-prep-hero__inner', {
      display: 'grid',
      'grid-template-columns': 'minmax(0, 1fr)',
      'min-height': '0'
    });

    setImportantAll('.bbq-prep-hero__visual', {
      order: '1',
      position: 'relative',
      height: heroHeight,
      'min-height': '430px',
      'max-height': '680px',
      overflow: 'hidden',
      margin: '0'
    });

    setImportantAll('.bbq-prep-hero__image', {
      position: 'absolute',
      inset: '0',
      display: 'block',
      width: '100%',
      'max-width': 'none',
      height: '100%',
      'max-height': 'none',
      'object-fit': 'cover',
      'object-position': 'center'
    });

    setImportantAll('.bbq-prep-hero__content', {
      order: '2',
      display: 'block',
      padding: `66px ${horizontalPadding} 78px`
    });

    setImportantAll('.bbq-prep-hero__content-inner', {
      margin: '0'
    });

    setImportantAll('.bbq-prep-hero__title', {
      width: '100%',
      'max-width': '760px',
      'font-size': viewportWidth <= 1000 ? '44px' : '50px',
      'line-height': '1.03',
      'overflow-wrap': 'normal',
      'word-break': 'normal'
    });

    setImportantAll('.bbq-prep-hero__lead', {
      width: '100%',
      'max-width': '720px',
      'overflow-wrap': 'normal',
      'word-break': 'normal'
    });

    /* ========================================================
       PURPOSE
       ======================================================== */

    setImportantAll(
      [
        '.bbq-prep-purpose',
        '.bbq-prep-purpose__inner',
        '.bbq-prep-purpose__layout',
        '.bbq-prep-purpose__media',
        '.bbq-prep-purpose__content'
      ].join(', '),
      {
        width: '100%',
        'max-width': '100%',
        'min-width': '0'
      }
    );

    setImportantAll('.bbq-prep-purpose', {
      padding: `80px ${horizontalPadding} 94px`
    });

    setImportantAll('.bbq-prep-purpose__layout', {
      display: 'grid',
      'grid-template-columns': 'minmax(0, 1fr)',
      gap: '50px',
      'align-items': 'start'
    });

    setImportantAll('.bbq-prep-purpose__media', {
      order: '1',
      position: 'relative',
      height: purposeHeight,
      'min-height': '420px',
      'max-height': '650px',
      overflow: 'hidden',
      margin: '0'
    });

    setImportantAll('.bbq-prep-purpose__image', {
      position: 'absolute',
      inset: '0',
      display: 'block',
      width: '100%',
      'max-width': 'none',
      height: '100%',
      'max-height': 'none',
      'object-fit': 'cover',
      'object-position': 'center'
    });

    setImportantAll('.bbq-prep-purpose__content', {
      order: '2',
      'max-width': '760px'
    });

    setImportantAll(
      [
        '.bbq-prep-purpose__title',
        '.bbq-prep-purpose__lead',
        '.bbq-prep-purpose__list'
      ].join(', '),
      {
        width: '100%',
        'max-width': '740px'
      }
    );

    setImportantAll('.bbq-prep-purpose__title', {
      'font-size': viewportWidth <= 1000 ? '42px' : '48px',
      'line-height': '1.03',
      'overflow-wrap': 'normal',
      'word-break': 'normal'
    });

    setImportantAll('.bbq-prep-purpose__item', {
      'grid-template-columns': '42px minmax(0, 1fr)',
      width: '100%',
      'min-width': '0'
    });

    setImportantAll(
      [
        '.bbq-prep-purpose__item > div',
        '.bbq-prep-purpose__item-title',
        '.bbq-prep-purpose__item-text'
      ].join(', '),
      {
        width: '100%',
        'max-width': '100%',
        'min-width': '0',
        'overflow-wrap': 'normal',
        'word-break': 'normal'
      }
    );

    setImportantAll('.bbq-prep-purpose__note', {
      width: '100%',
      'max-width': '760px',
      'margin-right': 'auto',
      'margin-left': '0'
    });

    /* ========================================================
       WORKSHOP
       ======================================================== */

    setImportantAll(
      [
        '.bbq-prep-workshop',
        '.bbq-prep-workshop__inner',
        '.bbq-prep-workshop__layout',
        '.bbq-prep-workshop__content',
        '.bbq-prep-workshop__media'
      ].join(', '),
      {
        width: '100%',
        'max-width': '100%',
        'min-width': '0'
      }
    );

    setImportantAll('.bbq-prep-workshop', {
      padding: `80px ${horizontalPadding} 94px`
    });

    setImportantAll('.bbq-prep-workshop__layout', {
      display: 'grid',
      'grid-template-columns': 'minmax(0, 1fr)',
      gap: '50px',
      'align-items': 'start'
    });

    setImportantAll('.bbq-prep-workshop__content', {
      order: '1',
      'max-width': '760px'
    });

    setImportantAll('.bbq-prep-workshop__media', {
      order: '2',
      position: 'relative',
      height: workshopHeight,
      'min-height': '500px',
      'max-height': '760px',
      overflow: 'hidden',
      margin: '0'
    });

    setImportantAll('.bbq-prep-workshop__image', {
      position: 'absolute',
      inset: '0',
      display: 'block',
      width: '100%',
      'max-width': 'none',
      height: '100%',
      'max-height': 'none',
      'object-fit': 'cover',
      'object-position': 'center 40%'
    });

    setImportantAll(
      [
        '.bbq-prep-workshop__title',
        '.bbq-prep-workshop__lead',
        '.bbq-prep-workshop__points'
      ].join(', '),
      {
        width: '100%',
        'max-width': '740px'
      }
    );

    setImportantAll('.bbq-prep-workshop__title', {
      'font-size': viewportWidth <= 1000 ? '42px' : '48px',
      'line-height': '1.03',
      'overflow-wrap': 'normal',
      'word-break': 'normal'
    });

    setImportantAll('.bbq-prep-workshop__point', {
      'grid-template-columns': '42px minmax(0, 1fr)',
      width: '100%',
      'min-width': '0'
    });

    setImportantAll(
      [
        '.bbq-prep-workshop__point > div',
        '.bbq-prep-workshop__point-title',
        '.bbq-prep-workshop__point-text'
      ].join(', '),
      {
        width: '100%',
        'max-width': '100%',
        'min-width': '0',
        'overflow-wrap': 'normal',
        'word-break': 'normal'
      }
    );
  };

  const requestPrepTabletLayout = () => {
    if (prepResizeFrame) return;

    prepResizeFrame = window.requestAnimationFrame(() => {
      prepResizeFrame = 0;
      forcePrepTabletLayout();
    });
  };

  const initialisePrepStationTabletLayout = () => {
    forcePrepTabletLayout();

    if (prepResizeListenerAttached) return;

    window.addEventListener('resize', requestPrepTabletLayout, {
      passive: true
    });

    window.addEventListener(
      'orientationchange',
      requestPrepTabletLayout,
      { passive: true }
    );

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        requestPrepTabletLayout,
        { passive: true }
      );
    }

    prepResizeListenerAttached = true;
  };

  const initialise = () => {
    setCustomLiquidWrappers();
    initialiseOverlayHeader();
    initialiseFooter();
    initialisePrepStationTabletLayout();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      { once: true }
    );
  } else {
    initialise();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      setCustomLiquidWrappers(event.target || document);
      initialiseFooter();
      requestPrepTabletLayout();
    }
  );
})();